<template>
  <div class="col dashboard-goals">
    <v-row>
      <v-col cols="12">
        <div class="text-h4 mb-2">Metas de vendas</div>
        <div class="text-subtitle-1 text--secondary">Acompanhe o realizado em relação às metas por produto e cliente.</div>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" text>
      Não foi possível carregar as metas.
    </v-alert>

    <v-row>
      <v-col v-for="group in groups" :key="group.key" cols="12">
        <div class="text-h5 mb-3">{{ group.title }}</div>
        <v-card class="mb-4" :loading="loading" outlined>
          <v-card-text>
            <v-data-table
              :headers="headersFor(group.key)"
              :items="itemsFor(group.key)"
              :loading="loading"
              :hide-default-footer="true"
              :mobile-breakpoint="0"
              dense
              :no-data-text="group.noDataText"
            >
              <template v-slot:item.monthlySoldQuantity="{ item }">
                {{ formatQuantity(item.monthlySoldQuantity) }}
              </template>
              <template v-slot:item.monthlyTotalQuantity="{ item }">
                {{ formatQuantity(item.monthlyTotalQuantity) }}
              </template>
              <template v-slot:item.monthlyProgress="{ item }">
                <div class="d-flex align-center progress-cell">
                  <v-progress-linear :value="progress(item.monthly)" :color="progressColor(item.monthly)" height="8" rounded />
                  <span class="caption ml-2">{{ progress(item.monthly) }}%</span>
                </div>
              </template>
              <template v-slot:item.quarterlySoldQuantity="{ item }">
                {{ formatQuantity(item.quarterlySoldQuantity) }}
              </template>
              <template v-slot:item.quarterlyTotalQuantity="{ item }">
                {{ formatQuantity(item.quarterlyTotalQuantity) }}
              </template>
              <template v-slot:item.quarterlyProgress="{ item }">
                <div class="d-flex align-center progress-cell">
                  <v-progress-linear :value="progress(item.quarterly)" :color="progressColor(item.quarterly)" height="8" rounded />
                  <span class="caption ml-2">{{ progress(item.quarterly) }}%</span>
                </div>
              </template>
              <template v-slot:item.annualSoldQuantity="{ item }">
                {{ formatQuantity(item.annualSoldQuantity) }}
              </template>
              <template v-slot:item.annualTotalQuantity="{ item }">
                {{ formatQuantity(item.annualTotalQuantity) }}
              </template>
              <template v-slot:item.annualProgress="{ item }">
                <div class="d-flex align-center progress-cell">
                  <v-progress-linear :value="progress(item.annual)" :color="progressColor(item.annual)" height="8" rounded />
                  <span class="caption ml-2">{{ progress(item.annual) }}%</span>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import gql from 'graphql-tag';

export default {
  data () {
    return {
      loading: true,
      error: false,
      dashboard: {
        byProduct: {},
        byCustomer: {}
      },
      groups: [
        { key: 'byProduct', title: 'Metas por produto', noDataText: 'Nenhum produto encontrado' },
        { key: 'byCustomer', title: 'Metas por cliente', noDataText: 'Nenhum cliente encontrado' }
      ],
      periods: [
        { key: 'monthly', title: 'Mensal' },
        { key: 'quarterly', title: 'Trimestral' },
        { key: 'annual', title: 'Anual' }
      ],
      headers: [
        { text: 'Produto', value: 'name', sortable: true, width: '200px', align: 'justify' },
        { text: 'Progresso Mensal', value: 'monthlyProgress', sortable: false, width: '100px' },
        { text: 'Progresso Trimestral', value: 'quarterlyProgress', sortable: false, width: '100px' },
        { text: 'Progresso Anual', value: 'annualProgress', sortable: false, width: '100px' }
      ]
    };
  },
  created () {
    this.loadDashboard();
  },
  methods: {
    loadDashboard () {
      this.loading = true;
      this.error = false;
      this.$apollo.query({
        query: DASHBOARD_GOAL_QUERY,
        fetchPolicy: 'no-cache'
      }).then(response => {
        this.dashboard = response.data.accountContext.dashboardGoal || this.dashboard;
        console.log('dashboard', this.dashboard);
      }).catch((e) => {
        this.error = true;
        console.log('error loading dashboard goals', e);
      }).finally(() => {
        this.loading = false;
      });
    },
    itemsFor (group) {
      const itemsByPeriod = {};
      this.periods.forEach(period => {
        itemsByPeriod[period.key] = (this.dashboard[group] && this.dashboard[group][period.key] && this.dashboard[group][period.key].items) || [];
      });

      const baseItems = this.periods.reduce((items, period) => items.length ? items : itemsByPeriod[period.key], []);
      return baseItems.map(item => {
        const row = { id: item.id, name: item.name };
        this.periods.forEach(period => {
          const periodItem = itemsByPeriod[period.key].find(candidate => candidate.id === item.id) || {};
          row[period.key] = periodItem;
          row[`${period.key}SoldQuantity`] = periodItem.soldQuantity;
          row[`${period.key}TotalQuantity`] = periodItem.totalQuantity;
        });
        return row;
      });
    },
    headersFor (group) {
      const entityHeader = { ...this.headers[0], text: group === 'byCustomer' ? 'Cliente' : 'Produto' };
      const periodHeaders = this.periods.reduce((headers, period) => headers.concat([
        { text: `${period.title} vendido`, value: `${period.key}SoldQuantity`, align: 'right', sortable: true },
        { text: `Meta ${period.title.toLowerCase()}`, value: `${period.key}TotalQuantity`, align: 'right', sortable: true },
        { text: `Progresso ${period.title.toLowerCase()}`, value: `${period.key}Progress`, sortable: false, width: '150px' }
      ]), []);
      return [entityHeader, ...periodHeaders];
    },
    formatQuantity (value) {
      return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(Number(value || 0));
    },
    progress (item) {
      const target = Number(item.totalQuantity || 0);
      if (target <= 0) return 0;
      return Math.min(100, Math.round((Number(item.soldQuantity || 0) / target) * 100));
    },
    progressColor (item) {
      const value = this.progress(item);
      return value >= 75 ? 'success' : value >= 33 ? 'warning' : 'error';
    }
  }
};

const DASHBOARD_GOAL_QUERY = gql`
  query dashboard_goal {
    accountContext {
      dashboardGoal
    }
  }
`;
</script>

<style scoped>
.progress-cell {
  min-width: 40px;
}
</style>